import { useMemo } from 'react';

import { TIMELINE_MAX_THUMBNAILS } from '../utils/constants';
import {
  calculateThumbnails,
  getEffectiveThumbnailsPerSecond,
  ThumbnailMetadata,
  VideoMetadata,
} from '../utils/videoUtils';

interface UseVideoThumbnailsProps {
  videoMetadata: VideoMetadata | null | undefined;
  stripHeight: number;
  containerWidth: number;
  thumbnailsPerSecond?: number;
}

interface UseVideoThumbnailsReturn {
  thumbnails: ThumbnailMetadata[];
  isReady: boolean;
}

export function useVideoThumbnails({
  videoMetadata,
  stripHeight,
  containerWidth,
  thumbnailsPerSecond = 0.5,
}: UseVideoThumbnailsProps): UseVideoThumbnailsReturn {
  const duration = videoMetadata?.duration ?? 0;

  const effectiveThumbnailsPerSecond = useMemo(() => {
    if (duration <= 0) return 0;
    return getEffectiveThumbnailsPerSecond(
      duration,
      thumbnailsPerSecond,
      TIMELINE_MAX_THUMBNAILS
    );
  }, [duration, thumbnailsPerSecond]);

  const thumbnails = useMemo(() => {
    if (
      effectiveThumbnailsPerSecond <= 0 ||
      stripHeight <= 0 ||
      containerWidth <= 0
    ) {
      return [];
    }
    return calculateThumbnails(
      duration,
      stripHeight,
      effectiveThumbnailsPerSecond,
      containerWidth
    );
  }, [duration, stripHeight, effectiveThumbnailsPerSecond, containerWidth]);

  return {
    thumbnails,
    isReady: Boolean(videoMetadata && thumbnails.length > 0),
  };
}
